import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/* Mobile-only Carbon Labs hero. Permanent Carbon Labs presentation —
 * no campaign-dated messaging. Layout: dominant mascot, cyan energy
 * backdrop, silver "Precision" headline, cyan "Research Peptides"
 * subhead, one primary CTA and one secondary link. */
export const MobileCarbonHero = () => {
    return (
        <section
            className="lg:hidden relative overflow-hidden"
            data-testid="mobile-carbon-hero"
            style={{ minHeight: "min(720px, calc(100dvh - var(--header-h)))" }}
        >
            {/* Deep carbon-fibre substrate */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(120% 80% at 50% 10%, rgba(0,229,255,0.10) 0%, transparent 55%)," +
                        "linear-gradient(180deg, rgba(6,8,10,0.98) 0%, #050708 100%)," +
                        "repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0 2px, rgba(0,0,0,0.35) 2px 4px)",
                    backgroundBlendMode: "normal, normal, overlay",
                }}
            />

            {/* Cyan X-motif carbon panels behind the mascot */}
            <div aria-hidden="true" className="absolute right-[-10%] top-[18%] w-[110%] h-[62%] pointer-events-none opacity-90">
                <div
                    className="absolute right-8 top-0 w-[62%] h-[42%] cfibre-panel-cyan rounded-sm"
                    style={{ transform: "skewX(-14deg)", boxShadow: "0 0 40px rgba(0,229,255,0.25)" }}
                />
                <div
                    className="absolute right-0 bottom-0 w-[70%] h-[42%] cfibre-panel-cyan rounded-sm"
                    style={{ transform: "skewX(-14deg)", boxShadow: "0 0 40px rgba(0,229,255,0.20)" }}
                />
            </div>

            {/* Vertical eyebrow — left rail */}
            <div className="absolute left-4 top-[24%] z-10 flex flex-col gap-1 max-w-[130px]">
                <span className="text-[10px] font-mono uppercase tracking-[0.4em] text-white/55 leading-[1.6]">
                    Engineered<br />for a<br />higher<br />standard.
                </span>
                <span className="mt-3 block w-8 h-px bg-[var(--cyan)] shadow-[0_0_6px_rgba(0,229,255,0.7)]" />
            </div>

            {/* Mascot — the visual hero */}
            <div className="relative z-10 pt-6 pb-4 flex items-start justify-center">
                <div className="relative w-[95%] max-w-[520px]">
                    {/* cyan radial glow behind mascot */}
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 blur-2xl pointer-events-none"
                        style={{
                            background: "radial-gradient(circle at 55% 45%, rgba(0,229,255,0.55) 0%, transparent 55%)",
                            transform: "scale(1.1)",
                        }}
                    />
                    <img
                        src="/brand/mascot.png"
                        alt="Carbon Labs mascot"
                        className="relative z-10 w-full h-auto object-contain drop-shadow-[0_0_36px_rgba(0,229,255,0.45)]"
                        loading="eager"
                        fetchPriority="high"
                        data-testid="mobile-hero-mascot"
                    />
                </div>
            </div>

            {/* Permanent Carbon Labs presentation */}
            <div className="relative z-10 container-x pb-10">
                <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--cyan)] shadow-[0_0_8px_rgba(0,229,255,0.8)]" />
                    <span
                        className="text-[10px] font-mono uppercase tracking-[0.32em] text-[var(--cyan-hi)]"
                        data-testid="mobile-hero-eyebrow"
                    >
                        // Carbon Labs · AU
                    </span>
                </div>

                <h1
                    className="font-display uppercase font-black leading-[0.9] tracking-tight text-metal-gold"
                    style={{
                        fontFamily: "'Chakra Petch', 'Rajdhani', 'Space Grotesk', Impact, sans-serif",
                        fontSize: "clamp(52px, 15vw, 88px)",
                        letterSpacing: "-0.02em",
                        textShadow: "0 1px 0 rgba(240,244,248,0.85), 0 2px 0 rgba(15,20,25,0.7), 0 6px 14px rgba(0,0,0,0.75), 0 0 26px rgba(0,229,255,0.30)",
                    }}
                    data-testid="mobile-hero-headline"
                >
                    Precision
                </h1>

                <div
                    className="uppercase font-black leading-[0.95] mt-1"
                    style={{
                        fontFamily: "'Chakra Petch', 'Rajdhani', sans-serif",
                        fontSize: "clamp(24px, 7vw, 40px)",
                        letterSpacing: "0.005em",
                        color: "var(--cyan-hi)",
                        textShadow: "0 0 22px rgba(0,229,255,0.55)",
                    }}
                    data-testid="mobile-hero-subhead"
                >
                    Research Peptides
                </div>

                <div
                    className="mt-3 uppercase text-white/70"
                    style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontWeight: 500,
                        letterSpacing: "0.28em",
                        fontSize: 11,
                    }}
                >
                    HPLC-verified · Cold-chain dispatch · Sydney AU
                </div>

                <div className="mt-7 flex items-end justify-between gap-4">
                    <div className="flex flex-col gap-2.5">
                        <Link
                            to="/shop"
                            className="inline-flex items-center gap-3 pl-6 pr-5 py-3.5 rounded-full border border-[var(--cyan)] bg-[rgba(0,229,255,0.10)] text-[var(--cyan-hi)] font-mono uppercase text-[11px] tracking-[0.22em] font-semibold hover:bg-[rgba(0,229,255,0.18)] hover:shadow-[0_0_18px_rgba(0,229,255,0.45)] transition-all"
                            data-testid="mobile-hero-cta"
                        >
                            Shop Products
                            <ArrowRight size={14} strokeWidth={2.4} />
                        </Link>
                        <Link
                            to="/lab-standards"
                            className="pl-1 text-[10px] font-mono uppercase tracking-[0.28em] text-white/60 hover:text-[var(--cyan-hi)] transition-colors"
                            data-testid="mobile-hero-cta-secondary"
                        >
                            Lab Standards →
                        </Link>
                    </div>

                    {/* Mini pillar column bottom-right */}
                    <div className="flex items-start gap-2 pr-1" aria-hidden="true">
                        <span className="mt-1 inline-block w-px h-14 bg-gradient-to-b from-transparent via-[var(--cyan)] to-transparent" />
                        <div className="flex flex-col gap-0.5">
                            {["Science", "Discipline", "Results"].map((w) => (
                                <span
                                    key={w}
                                    className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/55"
                                >
                                    {w}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
